/**
 * Queries des pages « tout ce que l'association propose sur une feature »
 * (`/associations/[slug]/<domaine>`, cf. `AssoListeFeature`).
 *
 * Couche 2a (ADR-001) : cache TanStack au-dessus de `associationListesApi`.
 * Rendu SSR (ADR-004) : le composant appelle `suspense()` côté serveur pour
 * que la PREMIÈRE page soit dans le HTML (cartes indexables).
 */
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import {
  associationListesApi,
  type DomaineAsso,
  type PageListeDomaine,
} from "~/services/api/organizations";

export type { DomaineAsso, PageListeDomaine } from "~/services/api/organizations";

export const assoListeKeys = {
  liste: (slug: string, domaine: DomaineAsso) =>
    ["asso-liste", slug, domaine] as const,
};

/**
 * Pagination par `useInfiniteQuery`, comme la page /recherche.
 *
 * ⚠️ POURQUOI PAS une accumulation maison (Karim 2026-09-12) : c'est
 * EXACTEMENT le mécanisme dont le site est déjà revenu. Cf.
 * `useRechercheData` : « l'ancien mécanisme (watch + rollback d'offset en cas
 * d'erreur) rebouclait sur un 429 → chargement à l'infini ». `useInfiniteQuery`
 * borne les réessais, tient `hasNextPage`/`fetchNextPage`, dédoublonne les
 * requêtes en vol et garde les pages déjà lues au retour sur la page.
 */
export function useAssoListeDomaine(params: {
  slug: MaybeRefOrGetter<string>;
  domaine: MaybeRefOrGetter<DomaineAsso>;
  taillePage: number;
}) {
  // Base résolue AU SETUP : la queryFn tourne aussi côté serveur (suspense),
  // hors du contexte Nuxt synchrone.
  const apiUrl = useRuntimeConfig().public.apiUrl as string;

  return useInfiniteQuery({
    queryKey: computed(() =>
      assoListeKeys.liste(toValue(params.slug), toValue(params.domaine)),
    ),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }): Promise<PageListeDomaine> =>
      associationListesApi.page(
        apiUrl,
        toValue(params.slug),
        toValue(params.domaine),
        { offset: pageParam as number, limit: params.taillePage },
        signal,
      ),
    getNextPageParam: (derniere: PageListeDomaine) => {
      const { offset, limit, total } = derniere.pagination;
      // Page vide = dérive entre le total et les lignes réelles : on s'arrête
      // plutôt que de redemander indéfiniment la même tranche.
      if (derniere.data.length === 0) return undefined;
      const suivant = offset + limit;
      return suivant >= total ? undefined : suivant;
    },
    // ⚠️ ANTI-BOUCLE : au plus un réessai, jamais d'acharnement sur un 429.
    retry: 1,
    retryDelay: 800,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
